'use client';

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { SavedRecipeItem } from '@hooks/recipe/useSavedRecipes';
import { useSaveRecipe } from '@hooks/recipe/useSaveRecipe';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import RecipeCard from '@repo/ui/src/components/ContentGrid/RecipeCard';
import UnsaveButton from './_UnsaveButton';

interface SavedRecipeCardProps {
  item: SavedRecipeItem;
}

const SavedRecipeCard = ({ item }: SavedRecipeCardProps) => {
  const { t } = useTranslation();
  const [removed, setRemoved] = useState(false);
  const { trigger } = useSaveRecipe({ contentId: item.contentId });

  const handleUndo = useCallback(async () => {
    try {
      await trigger();
      setRemoved(false);
    } catch {
      toast.error(
        t('savedRecipes.undoError', undefined, { default: 'Failed to restore recipe. Please try again.' }),
      );
    }
  }, [t, trigger]);

  const handleRemove = useCallback(() => {
    setRemoved(true);
    const title = item.content?.recipeMetaSection.title ?? '';
    toast(
      t('savedRecipes.removed', undefined, { default: '"{title}" has been removed' }).replace('{title}', title),
      {
        action: {
          label: t('savedRecipes.undo', undefined, { default: 'Undo' }),
          onClick: handleUndo,
        },
        duration: 5000,
      },
    );
  }, [handleUndo, item.content?.recipeMetaSection.title, t]);

  if (!item.content || removed) return null;

  return (
    <div className="relative" data-testid="saved-recipe-card">
      <UnsaveButton contentId={item.contentId} onUnsaved={handleRemove} />
      <RecipeCard
        _meta={item.content._meta}
        assets={item.content.assets}
        recipeMetaSection={item.content.recipeMetaSection}
        ratingSummary={item.content.ratingSummary}
      />
    </div>
  );
};

export default SavedRecipeCard;
